import { useMutation, useQuery } from "@apollo/client";
import { Box, Divider, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { FeatureFlagKey, FEATURE_FLAG_DEFS } from "encamp-shared/src/featureFlags";
import { useMemo } from "react";

import { gql } from "../../../../../generated-graphql";

const TENANT_FLAGS_QUERY = gql(`
  query TenantFeatureFlags($tenantId: ID!) {
    tenant(id: $tenantId) {
      id
      name
      featureFlags
    }
  }
`);

const SET_FLAG_MUTATION = gql(`
  mutation SetTenantFeatureFlag($tenantId: ID!, $key: String!, $value: Boolean!) {
    setTenantFeatureFlag(tenantId: $tenantId, key: $key, value: $value) {
      id
      featureFlags
    }
  }
`);

export function FeatureFlagsTab({ tenantId }: { tenantId: string }) {
  const { data, loading } = useQuery(TENANT_FLAGS_QUERY, {
    variables: { tenantId },
    fetchPolicy: "cache-and-network",
  });
  const [setFlag] = useMutation(SET_FLAG_MUTATION);

  const featureFlags: Record<string, boolean> = useMemo(() => {
    return (data?.tenant?.featureFlags ?? {}) as Record<string, boolean>;
  }, [data?.tenant?.featureFlags]);

  const handleToggle = (flagKey: string) => async (_event: unknown, checked: boolean) => {
    const currentFlags = (data?.tenant?.featureFlags ?? {}) as Record<string, boolean>;
    const optimisticFeatureFlags = {
      ...currentFlags,
      [flagKey]: checked,
    };
    await setFlag({
      variables: { tenantId, key: flagKey, value: checked },
      optimisticResponse: {
        setTenantFeatureFlag: {
          __typename: "Tenant",
          id: tenantId,
          featureFlags: optimisticFeatureFlags,
        },
      },
    });
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6">Feature flags</Typography>
        <Typography variant="body2" color="text.secondary">
          Toggle Encamp-managed feature flags for this organization. These override or replace
          LaunchDarkly settings over time.
        </Typography>
      </Box>
      <Divider />
      <Stack spacing={2}>
        {FEATURE_FLAG_DEFS.map((f) => (
          <Box key={f.key}>
            <FormControlLabel
              disabled={loading}
              control={<Switch checked={!!featureFlags?.[f.key]} onChange={handleToggle(f.key)} />}
              label={
                <Stack>
                  <Typography variant="body1">{f.label}</Typography>
                  {f.description && (
                    <Typography variant="caption" color="text.secondary">
                      {f.description}
                    </Typography>
                  )}
                </Stack>
              }
            />
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}
